import React, { useRef } from 'react';
import { makeStyles } from '@mui/styles';
import { Box, FilledInput, FormControl, FormHelperText, InputLabel, Select } from '@mui/material';
import { InputElementEvent } from '../model';

export function sortByLabel<T extends { label: string }>(items: T[]): T[] {
  return [...items].sort((a, b) => a.label.localeCompare(b.label));
}

export interface BaseSelectInputProps {
  value?: string;
  label: string;
  onChange?: (element: React.ChangeEvent) => void;
  valueChanged?: (value: string) => void;
  styleOverrides?: string;
  errorMessage?: string;
  isFilled?: boolean;
  type?: string;
  textColor?: string;
  disabled?: boolean;
  disableAlphabeticalSort?: boolean;
}

export const useSelectInputStyles = makeStyles((_theme) => ({
  textField: {
    height: '30px',
    width: '100%',
    margin: '0 0 4px',
  },
  helperText: {
    fontSize: '12px',
  },
}));

export const createSelectInputChangeHandler = (
  onChange?: (element: React.ChangeEvent) => void,
  valueChanged?: (value: string) => void,
) => {
  return (event: InputElementEvent): void => {
    if (onChange) {
      onChange(event);
    }
    if (valueChanged) {
      valueChanged(event.target.value);
    }
  };
};

interface SelectInputWrapperProps {
  label: string;
  value: string;
  errorMessage?: string;
  styleOverrides?: string;
  disabled?: boolean;
  classes: ReturnType<typeof useSelectInputStyles>;
  onChange: (event: InputElementEvent) => void;
  children: React.ReactNode;
}

export const SelectInputWrapper: React.FC<SelectInputWrapperProps> = ({
  label,
  value,
  errorMessage,
  styleOverrides,
  disabled = false,
  classes,
  onChange,
  children,
}) => {
  const rootRef = useRef<HTMLDivElement>(null);

  return (
    <Box style={{ marginBottom: 25, marginTop: -5 }}>
      <InputLabel htmlFor={label} shrink>
        {label}
      </InputLabel>
      <FormControl
        ref={rootRef}
        disabled={disabled}
        variant="filled"
        hiddenLabel
        error={errorMessage?.length > 0}
        className={`${styleOverrides} ${classes.textField}`}
        style={{ marginTop: -5 }}
      >
        <Select
          value={value}
          input={<FilledInput onChange={onChange} disableUnderline />}
          style={{ height: '35px' }}
          onClose={() => {
            setTimeout(() => {
              const active = document.activeElement as HTMLElement | null;
              if (active && rootRef.current?.contains(active)) active.blur();
            }, 0);
          }}
        >
          {children}
        </Select>
        {errorMessage && <FormHelperText className={classes.helperText}>{`${label} ${errorMessage}`}</FormHelperText>}
      </FormControl>
    </Box>
  );
};
